import { useCallback, useEffect, useState } from "react";
import { useGetIngredientWholeList, usePostIngredientData } from "../../../../api/ingredients";
import { IngredientSource } from "../../../../config/enums/IngredientType.enum";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { validateCharacterLength } from "../../../../utils/utils";
import { IngredientSelectionData } from "../types/ingredientRegisterView.types";

/**
 * Re-homes the ingredient-name-search + image-staging logic from the legacy
 * `IngredientDataRegister` (src/screens/ingredients/ingredientRegister/IngredientDataRegister.tsx),
 * scoped to the register flow only. That component's `isRegister` prop is
 * always `true` on this path, so image upload always stages via local state
 * rather than firing an immediate API call — the actual upload happens once
 * the ingredient exists, fired from `useSupplierSave`'s success callback
 * (`saveIngredientImage`). Not one of the 6 protected legacy hooks, so
 * re-homing this logic here does not violate the no-duplication rule.
 */
export const useIngredientNameSelection = () => {
  const [ingredientData, setIngredientData] = useState<IngredientSelectionData | undefined>(
    undefined
  );
  const [isCreatingNewIngredient, setIsCreatingNewIngredient] = useState(false);
  const [newIngredientName, setNewIngredientName] = useState("");
  const [newIngredientError, setNewIngredientError] = useState<string | undefined>(undefined);
  const [imageData, setImageData] = useState<File | null>(null);
  const [imagePath, setImagePath] = useState<string>("");

  const { data: ingredientListByProduct, refetch: refetchIngredientOptions } =
    useGetIngredientWholeList(true);

  const { mutateAsync: postIngredient, isLoading: isCreatingIngredient } = usePostIngredientData();

  useEffect(() => {
    setImagePath(
      ((ingredientData?.ingredient_data as Record<string, unknown>)
        ?.ingredient_image_path as string) || ""
    );
  }, [ingredientData]);

  // `value` is the selected option's id as a string (from `FormSearchSelect`'s
  // onChange — see src/components/ui/Input/FormSearchSelect/FormSearchSelect.tsx).
  // `useGetIngredientWholeList(true)` always prepends a real `{id: 0, title:
  // "+ New"}` entry to the list (see src/api/ingredients.ts), so "0" is a
  // regular, searchable option rather than a synthetic no-results fallback.
  const onSelectIngredient = useCallback(
    (value: string) => {
      if (value === "0") {
        setIsCreatingNewIngredient(true);
        setNewIngredientName("");
        setNewIngredientError(undefined);
        setIngredientData(undefined);
        return;
      }

      const selected = (ingredientListByProduct as Array<Record<string, unknown>> | undefined)?.find(
        option => String(option.id) === value
      );
      if (!selected) return;

      setIsCreatingNewIngredient(false);
      setIngredientData({
        ingredient_name: selected.title as string,
        ingredient_data: selected.ingredientData as Record<string, unknown>
      });
    },
    [ingredientListByProduct]
  );

  const onNewIngredientNameChange = useCallback((value: string) => {
    setNewIngredientName(value);
    const validation = validateCharacterLength(
      value,
      32,
      "Ingredient name cannot exceed 32 characters"
    );
    setNewIngredientError(validation === true ? undefined : validation);
  }, []);

  const onStartCreateIngredient = useCallback(() => {
    setIsCreatingNewIngredient(true);
    setNewIngredientName("");
    setNewIngredientError(undefined);
  }, []);

  const onCancelCreateIngredient = useCallback(() => {
    setIsCreatingNewIngredient(false);
    setNewIngredientName("");
    setNewIngredientError(undefined);
  }, []);

  const onCreateIngredient = useCallback(async () => {
    const trimmedName = newIngredientName.trim();
    if (!trimmedName || newIngredientError) return undefined;

    const data = await postIngredient({
      ingredient_name: trimmedName,
      ingredient_status: "ACTIVE",
      ingredient_source: IngredientSource.BILL_OF_MATERIAL
    });

    successMessage("Ingredient added successfully");
    setIsCreatingNewIngredient(false);
    refetchIngredientOptions();
    setIngredientData({ ingredient_name: data?.ingredient_name, ingredient_data: data });
    // Returned so the caller can push it into the search field's dedicated
    // RHF form (FormSearchSelect displays whatever id that field holds).
    return String(data?._id);
  }, [newIngredientName, newIngredientError, postIngredient, refetchIngredientOptions]);

  const onImageUpload = useCallback((file: File | null) => {
    setImageData(file);
    if (file) setImagePath(URL.createObjectURL(file));
  }, []);

  const onImageDelete = useCallback(() => {
    setImageData(null);
    setImagePath("");
  }, []);

  const resetIngredientSelection = useCallback(() => {
    setIngredientData(undefined);
    setIsCreatingNewIngredient(false);
    setNewIngredientName("");
    setNewIngredientError(undefined);
    setImageData(null);
    setImagePath("");
  }, []);

  return {
    ingredientData,
    setIngredientData,
    ingredientOptions: ingredientListByProduct || [],
    isCreatingNewIngredient,
    newIngredientName,
    newIngredientError,
    isCreatingIngredient,
    imageData,
    imagePath,
    onSelectIngredient,
    onNewIngredientNameChange,
    onStartCreateIngredient,
    onCancelCreateIngredient,
    onCreateIngredient,
    onImageUpload,
    onImageDelete,
    resetIngredientSelection
  };
};
